import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCapacity, useConflicts } from '../hooks/usePeople';
import { CapacityChart } from '../components/people/CapacityChart';
import { FadeIn, SkeletonChart } from '../components/ui/Skeleton';
import { Select } from '../components/ui/Select';
import { DateField } from '../components/ui/DateField';

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

export function CapacityPlanningPage() {
  const [startDate, setStartDate] = useState(todayISO());
  const [weeks, setWeeks] = useState(4);

  const window = { startDate, weeks };
  const { data: capacity, isLoading, isError } = useCapacity(window);
  const { data: conflicts } = useConflicts(window);

  return (
    <div className="w-full p-4 sm:p-6 lg:p-8">
      {/* Back link above the title, like the other detail pages; beside it the
          two-line title and the link fought for one row on a phone. */}
      <Link to="/people" className="text-sm text-blue-600 underline">
        ← Înapoi la director
      </Link>
      <h1 className="mt-2 text-2xl font-bold text-gray-900 sm:text-3xl">
        Planificarea capacității
      </h1>

      {/* Phones: date and weeks as two equal halves, the legend on its own
          row under them. */}
      <div className="mt-6 grid grid-cols-2 items-end gap-3 sm:flex sm:flex-wrap sm:gap-4">
        <label className="text-sm text-gray-600">
          Data de început
          <DateField value={startDate} onChange={setStartDate} className="mt-1 w-full sm:w-40" />
        </label>
        <div className="sm:w-40">
          <Select
            label="Săptămâni"
            value={String(weeks)}
            onChange={(v) => setWeeks(Number(v))}
            options={[2, 4, 8, 12].map((w) => ({ value: String(w), label: `${w} săptămâni` }))}
            className="w-full"
          />
        </div>

        <div className="col-span-2 flex items-center gap-4 text-xs text-gray-500 sm:ml-auto">
          <span className="flex items-center gap-1">
            <span className="inline-block h-3 w-3 rounded bg-gray-100" /> Disponibil
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block h-3 w-3 rounded bg-blue-500" /> Alocat
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block h-3 w-3 rounded ring-2 ring-red-500" /> Supraalocat
          </span>
        </div>
      </div>

      {conflicts && conflicts.length > 0 && (
        <div className="mt-6 rounded-lg border border-red-500/30 bg-red-500/10 p-4">
          <h2 className="font-semibold text-red-700 dark:text-red-400">
            Conflicte de resurse ({conflicts.length})
          </h2>
          <ul className="mt-2 space-y-1 text-sm text-red-700 dark:text-red-300">
            {conflicts.map((c) => (
              <li key={`${c.personId}-${c.weekStart}`}>
                <span className="font-medium">{c.personName}</span> este alocat {c.totalPercent}% (
                {c.overBy}% peste) pentru săptămâna {c.weekStart}.
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        {isLoading && <SkeletonChart bars={8} height={240} className="border-0 p-0" />}
        {isError && <p className="text-red-600">Nu s-au putut încărca datele de capacitate.</p>}
        {capacity && (
          <FadeIn>
            <CapacityChart data={capacity} />
          </FadeIn>
        )}
      </div>
    </div>
  );
}
